import { motion } from "framer-motion";
import { CalendarDays, MapPin, ArrowRight, Users, GraduationCap, Mic2, Video, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SignUpDialog from "@/components/SignUpDialog";
import { useSiteContent } from "@/hooks/useSiteContent";
import { getBlock } from "@/lib/siteContent";
import SaveButton from "@/components/saved/SaveButton";

const eventKey = (title: string, month: string, day: string) =>
  `${title}|${month}-${day}`.toLowerCase().replace(/[^a-z0-9|-]+/g, "-");

const eventCategories = [
  { label: "All", value: "all" },
  { label: "Training", value: "training", icon: GraduationCap },
  { label: "Webinars", value: "webinar", icon: Video },
  { label: "Workshops", value: "workshop", icon: Users },
  { label: "Conferences", value: "conference", icon: Mic2 },
];

const campaigns = [
  {
    tag: "Active Campaign",
    title: "Alabama Rural Health Initiative 2025",
    description: "A statewide campaign to improve healthcare access in underserved rural counties through coordinated advocacy, grant funding, and community engagement.",
    progress: 65,
    goal: "$2.5M in coordinated funding",
    partners: 12,
    endDate: "Aug 2025",
  },
  {
    tag: "Launching Soon",
    title: "Education Access Alliance: Closing the Digital Divide",
    description: "Partnering with school districts and nonprofits to secure technology funding and bridge connectivity gaps across Alabama's Black Belt region.",
    progress: 20,
    goal: "$1.8M in grants identified",
    partners: 8,
    endDate: "Dec 2025",
  },
  {
    tag: "Active Campaign",
    title: "Workforce Reentry & Second Chance Employment",
    description: "A multi-stakeholder campaign connecting returning citizens with job training, certification programs, and employer partnerships across the state.",
    progress: 45,
    goal: "500 placements by year-end",
    partners: 15,
    endDate: "Nov 2025",
  },
];

const featuredEvents = [
  {
    month: "Apr",
    day: "12",
    weekday: "Sat",
    title: "Alabama Rural Health Initiative — Community Kickoff",
    description: "Join our partners for the official launch of the Rural Health Initiative. Meet local health leaders, hear from grant recipients, and learn how to get involved.",
    location: "Selma, AL",
    time: "10:00 AM – 2:00 PM CT",
    partner: "Alabama Rural Health Association",
  },
  {
    month: "May",
    day: "03",
    weekday: "Sat",
    title: "Digital Divide Summit: Technology Access for Every Student",
    description: "A day-long summit bringing together educators, technologists, and funders to map out a connected future for Alabama's underserved school districts.",
    location: "Tuscaloosa, AL",
    time: "9:00 AM – 4:00 PM CT",
    partner: "Black Belt Community Foundation",
  },
  {
    month: "Jun",
    day: "14",
    weekday: "Sat",
    title: "Second Chance Job Fair & Resource Expo",
    description: "Connecting returning citizens with employers, training programs, and wraparound services. Open to the public — all are welcome.",
    location: "Birmingham, AL",
    time: "10:00 AM – 3:00 PM CT",
    partner: "Alabama Reentry Council",
  },
];

const trainingEvents = [
  {
    month: "Apr",
    day: "08",
    weekday: "Tue",
    title: "Rural Healthcare Funding Summit: Aligning Grants with Community Needs",
    description: "Join healthcare advocates and grant specialists to explore strategies for aligning federal and state funding with rural community health priorities.",
    location: "Virtual Event",
    time: "1:00 PM – 3:00 PM CT",
    category: "webinar",
    audience: "Advocates, Funders",
  },
  {
    month: "Apr",
    day: "17",
    weekday: "Thu",
    title: "Nonprofit Capacity Building Workshop: From Data to Grant Applications",
    description: "Hands-on training on using The Emerging Market's data tools to build stronger, evidence-backed grant applications for your organization.",
    location: "Birmingham, AL",
    time: "9:00 AM – 12:00 PM CT",
    category: "workshop",
    audience: "Agencies, Advocates",
  },
  {
    month: "Apr",
    day: "29",
    weekday: "Tue",
    title: "Legislative Impact Roundtable: Q1 Policy Review and Community Implications",
    description: "A collaborative session reviewing Q1 legislation and analyzing its projected impact on Alabama communities across key issue areas.",
    location: "Montgomery, AL",
    time: "2:00 PM – 4:00 PM CT",
    category: "conference",
    audience: "Policymakers, Advocates",
  },
  {
    month: "May",
    day: "14",
    weekday: "Wed",
    title: "Federal Grant Landscape Webinar: New Opportunities for Alabama Organizations",
    description: "An overview of newly released federal grant opportunities and how Alabama-based organizations can position themselves for success.",
    location: "Virtual Event",
    time: "11:00 AM – 12:30 PM CT",
    category: "webinar",
    audience: "Funders, Agencies",
  },
  {
    month: "May",
    day: "22",
    weekday: "Thu",
    title: "Grant Writing Fundamentals: A Training for Emerging Nonprofits",
    description: "Step-by-step training covering grant research, narrative writing, budgeting, and submission best practices for organizations new to the funding landscape.",
    location: "Huntsville, AL",
    time: "10:00 AM – 2:00 PM CT",
    category: "training",
    audience: "Agencies, Advocates",
  },
  {
    month: "Jun",
    day: "05",
    weekday: "Thu",
    title: "Data-Driven Advocacy Workshop: Turning County Insights into Policy Action",
    description: "Learn how to leverage county-level demographic and economic data to build compelling policy briefs and advocacy campaigns.",
    location: "Mobile, AL",
    time: "9:00 AM – 12:00 PM CT",
    category: "workshop",
    audience: "Policymakers, Advocates",
  },
];

const Events = () => {
  const { blocks: copy } = useSiteContent("events");
  const pageIntro = getBlock(
    copy,
    "page_intro",
    "Launch campaigns, spotlight partner events, and access training designed to strengthen capacity across Alabama's advocacy, policy, business, and community groups.",
  );
  const [signUpOpen, setSignUpOpen] = useState(false);

  const { data: partnerEvents = [] } = useQuery({
    queryKey: ["public-partner-events"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partner_events")
        .select("id,title,description,location,url,starts_at,ends_at,focus_area,kind,partner_org_id,affiliates:partner_org_id(name,slug)")
        .eq("published", true)
        .gte("starts_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
        .order("starts_at", { ascending: true })
        .limit(60);
      if (error) throw error;
      return data ?? [];
    },
  });

  const partnerCampaigns = (partnerEvents as any[]).filter((e) => e.kind === "campaign");
  const partnerRegularEvents = (partnerEvents as any[]).filter((e) => !e.kind || e.kind === "event");
  const partnerTrainings = (partnerEvents as any[]).filter((e) => e.kind === "training");

  const renderPartnerCard = (event: any, i: number) => {
    const start = new Date(event.starts_at);
    const month = start.toLocaleString("en-US", { month: "short" });
    const day = String(start.getDate()).padStart(2, "0");
    const weekday = start.toLocaleString("en-US", { weekday: "short" });
    const time = start.toLocaleString("en-US", { hour: "numeric", minute: "2-digit" });
    const partnerName = event.affiliates?.name ?? "Partner";
    const href = event.url || (event.affiliates?.slug ? `/partners/${event.affiliates.slug}` : "/partners");
    return (
      <motion.div
        key={event.id}
        initial={{ opacity: 0, x: -16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.05 + i * 0.05 }}
        className="group flex items-start gap-5 p-6 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors"
      >
        <div className="shrink-0 w-16 text-center">
          <div className="text-[11px] font-semibold tracking-wider uppercase text-primary">{month}</div>
          <div className="font-display text-3xl font-bold leading-tight">{day}</div>
          <div className="text-[11px] text-muted-foreground">{weekday}</div>
        </div>
        <div className="w-px self-stretch bg-border group-hover:bg-primary/30 transition-colors" />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            {event.focus_area && (
              <span className="text-[11px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full capitalize">
                {event.focus_area}
              </span>
            )}
            <span className="text-[11px] text-muted-foreground">Hosted by {partnerName}</span>
          </div>
          <h3 className="font-display font-semibold text-[15px] leading-snug group-hover:text-primary transition-colors mb-2">
            {event.title}
          </h3>
          {event.description && (
            <p className="text-sm text-muted-foreground leading-relaxed mb-3 line-clamp-3">
              {event.description}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {event.location && (
              <span className="flex items-center gap-1.5">
                <MapPin className="h-3 w-3" />
                {event.location}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <CalendarDays className="h-3 w-3" />
              {time}
            </span>
          </div>
        </div>
        <a href={href} target={event.url ? "_blank" : undefined} rel="noreferrer" className="shrink-0 hidden sm:inline-flex">
          <Button variant="outline" size="sm" className="items-center gap-1.5">
            Details
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </a>
        <SaveButton
          variant="icon"
          className="shrink-0"
          item={{
            item_type: "event",
            item_id: `partner-event-${event.id}`,
            title: event.title,
            url: href,
            source: partnerName,
            summary: event.description ?? undefined,
            metadata: {
              starts_at: event.starts_at,
              ends_at: event.ends_at,
              location: event.location,
              focus_area: event.focus_area,
              kind: event.kind,
              partner_org_id: event.partner_org_id,
            },
          }}
        />
      </motion.div>
    );
  };


  return (
    <div className="min-h-screen bg-background">
      <SignUpDialog open={signUpOpen} onOpenChange={setSignUpOpen} />
      <SiteHeader />
      <main id="main">

      {/* Hero */}
      <section className="pt-36 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }} />
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Events & Campaigns</span>
            <h1 className="font-display text-4xl md:text-5xl font-bold mt-3 mb-5">
              Campaigns, Partner Events & <span className="text-gradient-primary">Training</span>
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              {pageIntro}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Announcements */}
      <section className="py-16 border-t border-border">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-card to-accent/5 p-8 md:p-12 overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10 max-w-3xl">
              <span className="inline-block text-[11px] font-semibold tracking-wider uppercase px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">
                📢 Announcement
              </span>
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
                Fall 2026 Cohort: Now Accepting Interest
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6 max-w-2xl">
                The Emerging Market is opening its doors to new members and projects this Fall 2026. Whether you're a nonprofit seeking grant support, an advocate driving policy change, or a funder looking to maximize impact — our upcoming cohort is designed to onboard partners into the ecosystem and connect them with the tools, data, and collaboration they need to succeed.
              </p>
              <div className="flex flex-wrap gap-4 items-center">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-glow" onClick={() => setSignUpOpen(true)}>
                  Express Interest
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground">Applications open Summer 2026</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Campaigns Section */}
      <section className="py-16 border-t border-border">
        <div className="container mx-auto px-6">
          <div className="mb-10">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Campaigns</span>
            <h2 className="font-display text-3xl font-bold mt-2">Active Campaigns</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Multi-stakeholder initiatives coordinated by The Emerging Market to drive measurable impact across Alabama communities.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {campaigns.map((campaign, i) => (
              <motion.div
                key={campaign.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08 }}
                className="group rounded-xl border border-border bg-card p-6 hover:border-primary/30 transition-colors"
              >
                <span className={`inline-block text-[11px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full mb-4 ${campaign.tag === "Active Campaign" ? "bg-primary/10 text-primary" : "bg-accent text-accent-foreground"}`}>
                  {campaign.tag}
                </span>
                <h3 className="font-display font-semibold text-base leading-snug mb-2 group-hover:text-primary transition-colors">
                  {campaign.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {campaign.description}
                </p>
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                    <span>{campaign.goal}</span>
                    <span className="font-semibold text-foreground">{campaign.progress}%</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-primary"
                      initial={{ width: 0 }}
                      animate={{ width: `${campaign.progress}%` }}
                      transition={{ duration: 1, delay: 0.3 + i * 0.1 }}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Users className="h-3 w-3" />
                    {campaign.partners} partners
                  </span>
                  <span className="flex items-center gap-1.5">
                    <CalendarDays className="h-3 w-3" />
                    Ends {campaign.endDate}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Partner Events */}
      <section className="py-16 border-t border-border bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="mb-10">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Partner Spotlight</span>
            <h2 className="font-display text-3xl font-bold mt-2">Featured Events</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Highlighted events from our partner network — community gatherings, summits, and expos driving change across the state.
            </p>
          </div>
          <div className="space-y-4 max-w-4xl">
            {featuredEvents.map((event, i) => (
              <motion.div
                key={event.title}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }}
                className="group flex items-start gap-5 p-6 rounded-xl border border-primary/20 bg-card hover:border-primary/40 transition-colors cursor-pointer relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
                <div className="shrink-0 w-16 text-center pl-2">
                  <div className="text-[11px] font-semibold tracking-wider uppercase text-primary">
                    {event.month}
                  </div>
                  <div className="font-display text-3xl font-bold leading-tight">
                    {event.day}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{event.weekday}</div>
                </div>
                <div className="w-px self-stretch bg-border group-hover:bg-primary/30 transition-colors" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-[11px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Star className="h-3 w-3" /> Featured
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      Hosted by {event.partner}
                    </span>
                  </div>
                  <h3 className="font-display font-semibold text-[15px] leading-snug group-hover:text-primary transition-colors mb-2">
                    {event.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                    {event.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <MapPin className="h-3 w-3" />
                      {event.location}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <CalendarDays className="h-3 w-3" />
                      {event.time}
                    </span>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="shrink-0 hidden sm:flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  Details
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
                <SaveButton
                  variant="icon"
                  className="shrink-0"
                  item={{
                    item_type: "event",
                    item_id: eventKey(event.title, event.month, event.day),
                    title: event.title,
                    url: "/events",
                    source: event.partner,
                    summary: event.description,
                    metadata: {
                      month: event.month,
                      day: event.day,
                      weekday: event.weekday,
                      location: event.location,
                      time: event.time,
                      featured: true,
                    },
                  }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Campaigns */}
      {partnerCampaigns.length > 0 && (
        <section className="py-16 border-t border-border">
          <div className="container mx-auto px-6">
            <div className="mb-10">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">From the Network</span>
              <h2 className="font-display text-3xl font-bold mt-2">Partner Campaigns</h2>
              <p className="text-muted-foreground mt-2 max-w-2xl">
                Long-running campaigns and initiatives led by partner organizations in the Emerging Market network.
              </p>
            </div>
            <div className="space-y-4 max-w-4xl">
              {partnerCampaigns.map(renderPartnerCard)}
            </div>
          </div>
        </section>
      )}

      {/* Partner Events */}
      {partnerRegularEvents.length > 0 && (
        <section className="py-16 border-t border-border">
          <div className="container mx-auto px-6">
            <div className="mb-10">
              <span className="text-primary text-sm font-semibold tracking-wider uppercase">From the Network</span>
              <h2 className="font-display text-3xl font-bold mt-2">Partner Events</h2>
              <p className="text-muted-foreground mt-2 max-w-2xl">
                Upcoming gatherings, summits, and community events posted directly by partner organizations.
              </p>
            </div>
            <div className="space-y-4 max-w-4xl">
              {partnerRegularEvents.map(renderPartnerCard)}
            </div>
          </div>
        </section>
      )}

      {/* Training category filters */}
      <section className="border-t border-border bg-muted/20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-2 overflow-x-auto">
            {eventCategories.map((cat) => (
              <button
                key={cat.value}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border border-border bg-card text-muted-foreground hover:border-primary/30 hover:text-foreground transition-colors whitespace-nowrap first:bg-primary first:text-primary-foreground first:border-primary"
              >
                {cat.icon && <cat.icon className="h-3.5 w-3.5" />}
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Training & Education Events */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="mb-10">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Training & Education</span>
            <h2 className="font-display text-3xl font-bold mt-2">Upcoming Training</h2>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Workshops, webinars, and conferences to build skills and strengthen capacity across Alabama's nonprofit and advocacy communities.
            </p>
          </div>
          <div className="space-y-4 max-w-4xl">
            {partnerTrainings.map(renderPartnerCard)}
            {trainingEvents.map((event, i) => (

              <motion.div
                key={event.title}
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + i * 0.06 }}
                className="group flex items-start gap-5 p-6 rounded-xl border border-border bg-card hover:border-primary/30 transition-colors cursor-pointer"
              >
                <div className="shrink-0 w-16 text-center">
                  <div className="text-[11px] font-semibold tracking-wider uppercase text-primary">
                    {event.month}
                  </div>
                  <div className="font-display text-3xl font-bold leading-tight">
                    {event.day}
                  </div>
                  <div className="text-[11px] text-muted-foreground">{event.weekday}</div>
                </div>
                <div className="w-px self-stretch bg-border group-hover:bg-primary/30 transition-colors" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-[11px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full capitalize">
                      {event.category}
                    </span>
                    <span className="text-[11px] text-muted-foreground">
                      {event.audience}
                    </span>
                  </div>
                  <h3 className="font-display font-semibold text-[15px] leading-snug group-hover:text-primary transition-colors mb-2">
                    {event.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                    {event.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <MapPin className="h-3 w-3" />
                      {event.location}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <CalendarDays className="h-3 w-3" />
                      {event.time}
                    </span>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="shrink-0 hidden sm:flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  Register
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
                <SaveButton
                  variant="icon"
                  className="shrink-0"
                  item={{
                    item_type: "event",
                    item_id: eventKey(event.title, event.month, event.day),
                    title: event.title,
                    url: "/events",
                    source: event.category,
                    summary: event.description,
                    metadata: {
                      month: event.month,
                      day: event.day,
                      weekday: event.weekday,
                      location: event.location,
                      time: event.time,
                      category: event.category,
                      audience: event.audience,
                    },
                  }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      </main>
      <SiteFooter />
    </div>
  );
};

export default Events;
